/*
 * Активные алерты Prometheus (vmalert) кластера: чтение правил через proxy бэкенда
 * `/prometheus/<кластер>/api/rules`, разбор в плоский список и привязка алертов к
 * сущностям каталога по меткам. Привязка эвристическая: у алертов kube-state-metrics и
 * операторов нет метки «сущность», поэтому ресурс узнаётся по namespace и по имени в
 * метках объекта (pod/deployment/… начинаются с имени ресурса). Чистые функции покрыты
 * юнит-тестами.
 */
import { Entity } from '@backstage/catalog-model';
import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  AIRFLOW_TYPE,
  BROKER_TYPE,
  DATABASE_TYPE,
  DEFECTDOJO_TYPE,
  RUSTFS_TYPE,
  GITLAB_TYPE,
  KUBERNETES_CLUSTER_TYPE,
  ML_MODEL_TYPE,
  MLFLOW_MODEL_NAME_ANNOTATION,
  MONGODB_TYPE,
  RABBITMQ_TYPE,
  SERVICE_TYPE,
  VALKEY_TYPE,
  annotation,
  datastoreNameOf,
  entityClusters,
} from './viva';

export type PromAlert = {
  name: string;
  /** firing | pending (vmalert отдаёт ещё inactive у правил без алертов — их не берём). */
  state: string;
  severity?: string;
  labels: Record<string, string>;
  annotations: Record<string, string>;
  activeAt?: string;
  value?: string;
};

/** Ответ /api/v1/rules?type=alert (Prometheus и vmalert). */
export type RulesResponse = {
  status: string;
  error?: string;
  data?: {
    groups: {
      name: string;
      rules: {
        name: string;
        state?: string;
        labels?: Record<string, string>;
        annotations?: Record<string, string>;
        alerts?: {
          state: string;
          labels?: Record<string, string>;
          annotations?: Record<string, string>;
          activeAt?: string;
          value?: string;
        }[];
      }[];
    }[];
  };
};

const SEVERITY_RANK: Record<string, number> = {
  critical: 0,
  error: 1,
  warning: 2,
  info: 3,
};
const STATE_RANK: Record<string, number> = { firing: 0, pending: 1 };

export const severityRank = (severity?: string): number =>
  SEVERITY_RANK[severity ?? ''] ?? 4;

/**
 * Активные алерты из ответа rules: без служебных (severity none — Watchdog,
 * InfoInhibitor), firing раньше pending, дальше по важности и имени.
 */
export const activeAlerts = (rules: RulesResponse): PromAlert[] => {
  const result: PromAlert[] = [];
  for (const group of rules.data?.groups ?? []) {
    for (const rule of group.rules) {
      // vmalert не отдаёт alerts у правил без активных алертов — Prometheus отдаёт []
      for (const alert of rule.alerts ?? []) {
        const labels = { ...rule.labels, ...alert.labels };
        if (labels.severity === 'none') continue;
        result.push({
          name: labels.alertname ?? rule.name,
          state: alert.state,
          severity: labels.severity,
          labels,
          annotations: { ...rule.annotations, ...alert.annotations },
          activeAt: alert.activeAt,
          value: alert.value,
        });
      }
    }
  }
  return result.sort(
    (a, b) =>
      (STATE_RANK[a.state] ?? 2) - (STATE_RANK[b.state] ?? 2) ||
      severityRank(a.severity) - severityRank(b.severity) ||
      a.name.localeCompare(b.name),
  );
};

export async function fetchAlerts(
  apis: { discoveryApi: DiscoveryApi; fetchApi: FetchApi },
  cluster: string,
): Promise<PromAlert[]> {
  const proxy = await apis.discoveryApi.getBaseUrl('proxy');
  const response = await apis.fetchApi.fetch(
    `${proxy}/prometheus/${encodeURIComponent(cluster)}/api/rules?type=alert`,
  );
  if (!response.ok) {
    throw new Error(`Prometheus ${cluster}: HTTP ${response.status}`);
  }
  const body: RulesResponse = await response.json();
  if (body.status !== 'success') {
    throw new Error(`Prometheus ${cluster}: ${body.error ?? body.status}`);
  }
  return activeAlerts(body);
}

/** Однострочная суть алерта: summary, message или description, иначе имя. */
export const alertSummary = (a: PromAlert): string =>
  a.annotations.summary ??
  a.annotations.message ??
  a.annotations.description ??
  a.name;

/** Подробности для раскрытой строки: description, если он не совпадает с сутью. */
export const alertDescription = (a: PromAlert): string | undefined => {
  const d = a.annotations.description;
  return d && d !== alertSummary(a) ? d : undefined;
};

/**
 * Метки, по которым узнаётся ресурс: точное совпадение с именем. app — метка целей VMProbe
 * (проверки доступности blackbox: чарты app и model-serving), у алертов Probe* нет deployment/pod.
 */
const NAME_LABELS = [
  'app',
  'deployment',
  'statefulset',
  'daemonset',
  'cnpg_cluster',
  'strimzi_io_cluster',
  'kafka_cluster',
  'inferenceservice',
  'model',
  'airflow_instance',
  // хранилища на операторах MongoDB / RabbitMQ / Valkey: метка VMPodScrape и правил чартов
  'viva_datastore',
];
/** Метки объектов, имя которых начинается с имени ресурса (pod analytics-1, svc analytics-rw). */
const PREFIX_LABELS = [
  'pod',
  'service',
  'job_name',
  'deployment',
  'statefulset',
];

/** Алерт упоминает ресурс name в метках объекта. */
export const mentions = (labels: Record<string, string>, name: string) =>
  NAME_LABELS.some(l => labels[l] === name) ||
  PREFIX_LABELS.some(l => labels[l]?.startsWith(`${name}-`));

export type AlertMatcher = (alert: PromAlert) => boolean;

/**
 * Окружения сервинга модели (namespace'ы) в кластере по аннотациям шаблона ml-model:
 * staging-cluster и prod-clusters. Без кластера или у модели без этих аннотаций — только
 * production (сводка на главной и старые модели в одном кластере).
 */
const modelEnvsOn = (entity: Entity, cluster?: string): string[] => {
  const clusters = entityClusters(entity);
  const hasEnvClusters = clusters.some(c =>
    c.roles.some(r => r === 'staging' || r === 'production'),
  );
  if (!cluster || !hasEnvClusters) return ['production'];
  const roles = clusters.find(c => c.cluster === cluster)?.roles ?? [];
  return roles.filter(r => r === 'staging' || r === 'production');
};

/**
 * Какие алерты Prometheus кластера относятся к сущности; undefined — у сущности нет
 * привязки к кластеру. У самого кластера — все его алерты.
 */
export const alertMatcherFor = (
  entity: Entity,
  /** Кластер, чьи алерты фильтруем (вкладка «Метрики»); без него — сводка на главной. */
  cluster?: string,
): AlertMatcher | undefined => {
  const type = entity.spec?.type;
  const name = entity.metadata.name;
  if (entity.kind === 'Resource' && type === KUBERNETES_CLUSTER_TYPE) {
    return () => true;
  }
  if (
    type === DATABASE_TYPE ||
    type === BROKER_TYPE ||
    type === MONGODB_TYPE ||
    type === RABBITMQ_TYPE ||
    type === VALKEY_TYPE
  ) {
    const ds = datastoreNameOf(entity);
    return a => a.labels.namespace === 'datastores' && mentions(a.labels, ds);
  }
  if (type === SERVICE_TYPE) {
    return a =>
      /^(dev|stage|prod|feat-.*)$/.test(a.labels.namespace ?? '') &&
      mentions(a.labels, name);
  }
  if (type === ML_MODEL_TYPE) {
    const model = annotation(entity, MLFLOW_MODEL_NAME_ANNOTATION) ?? name;
    const envs = modelEnvsOn(entity, cluster);
    return a =>
      envs.includes(a.labels.namespace ?? '') &&
      (mentions(a.labels, model) || mentions(a.labels, name));
  }
  if (entity.kind === 'Resource' && type === GITLAB_TYPE) {
    // метрики, проба и правила GitLab платформы помечены gitlab="platform"
    return a => a.labels.gitlab === 'platform';
  }
  if (type === DEFECTDOJO_TYPE) {
    // правила чарта defectdojo-instance агрегируют (sum) и теряют namespace — узнаём по имени
    return a =>
      a.labels.namespace === 'defectdojo' ||
      /^DefectDojo/.test(a.labels.alertname ?? '');
  }
  if (type === RUSTFS_TYPE) {
    // правила RustFS (manifests/monitoring/rustfs.hosts.yaml) ставят namespace rustfs сами
    return a =>
      a.labels.namespace === 'rustfs' || /^RustFS/.test(a.labels.alertname ?? '');
  }
  if (type === AIRFLOW_TYPE) {
    const instance = name.replace(/^airflow-/, '');
    return a =>
      a.labels.airflow_instance === instance ||
      a.labels.namespace === `airflow-${instance}`;
  }
  return undefined;
};

export type AlertCount = { firing: number; pending: number };

export const countAlerts = (alerts: PromAlert[]): AlertCount => ({
  firing: alerts.filter(a => a.state === 'firing').length,
  pending: alerts.filter(a => a.state === 'pending').length,
});

/** Кластер платформы, чей Prometheus показывает сущность (undefined — нет привязки). */
export const alertClusterOf = (entity: Entity): string | undefined =>
  entityClusters(entity)[0]?.cluster;

export type ResourceAlerts = {
  entity: Entity;
  alerts: PromAlert[];
  count: AlertCount;
};

/**
 * Алерты по ресурсам для сводки на главной: кластер получает все алерты своего
 * Prometheus, остальные сущности — свою часть по alertMatcherFor. Ресурсы без алертов
 * не попадают; порядок — по числу firing, затем pending.
 */
export const alertsByResource = (
  clusters: { entity: Entity; alerts?: PromAlert[] }[],
  resources: Entity[],
): ResourceAlerts[] => {
  const byCluster = new Map(
    clusters.map(c => [c.entity.metadata.name, c.alerts ?? []]),
  );
  const rows: ResourceAlerts[] = [];
  for (const entity of [...clusters.map(c => c.entity), ...resources]) {
    const cluster = alertClusterOf(entity);
    const match = alertMatcherFor(entity);
    if (!cluster || !match) continue;
    const alerts = (byCluster.get(cluster) ?? []).filter(match);
    if (alerts.length === 0) continue;
    rows.push({ entity, alerts, count: countAlerts(alerts) });
  }
  return rows.sort(
    (a, b) =>
      b.count.firing - a.count.firing || b.count.pending - a.count.pending,
  );
};
